// src/theme/merge-components.js
//
// Merge a brand plugin's MUI component overrides INTO the base overrides of create-theme.js
// (spec 20260930-caro-editorial-theme, T026, IR-1, plan §7.3 step 5).
//
// A shallow `{ ...base, ...plugin }` replaces whole components and silently drops the functional
// base overrides (reduced motion, focus ring, touch padding, compact rows, the `interactive`
// card variant, glass). Here, per component:
//   - defaultProps     shallow merge, plugin wins
//   - styleOverrides   deep merge per slot, plugin wins on conflicting properties
//   - variants         concatenated, base first
//   - anything else    plugin wins
//
// Style callbacks (`({ theme, ownerState }) => ({...})`) are first-class: when either side of a
// merge is a function, the result is a callback that resolves both sides with the same props and
// deep-merges the results. @mui/utils' deepmerge cannot do this (it lets a function replace an
// object, research R-3) and is not a direct portal dependency anyway — owner decision D-3.

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;

const resolve = (value, props) => (typeof value === 'function' ? value(props) : value);

/** Deep-merge `source` into a copy of `target`. Source wins; arrays are replaced; callbacks combine. */
export function deepmerge(target, source) {
  if (typeof target === 'function' || typeof source === 'function') {
    return (props) => deepmerge(resolve(target, props), resolve(source, props));
  }
  if (!isPlainObject(target) || !isPlainObject(source)) {
    return source === undefined ? target : source;
  }
  const out = { ...target };
  for (const key of Object.keys(source)) {
    const s = source[key];
    const t = out[key];
    const bothMergeable =
      (isPlainObject(t) || typeof t === 'function') && (isPlainObject(s) || typeof s === 'function');
    out[key] = key in out && bothMergeable ? deepmerge(t, s) : s;
  }
  return out;
}

function mergeStyleOverrides(base, plugin) {
  if (base === undefined) return plugin;
  if (plugin === undefined) return base;
  if (typeof base === 'function' || typeof plugin === 'function') return deepmerge(base, plugin);
  const out = { ...base };
  for (const slot of Object.keys(plugin)) {
    out[slot] = slot in base ? deepmerge(base[slot], plugin[slot]) : plugin[slot];
  }
  return out;
}

/** Merge two MUI `components` maps. Components present on one side only pass through as-is. */
export function mergeComponents(base = {}, plugin = {}) {
  if (!plugin) return base;
  const out = { ...base };
  for (const name of Object.keys(plugin)) {
    const b = base[name];
    const p = plugin[name];
    if (!b) {
      out[name] = p;
      continue;
    }
    const merged = { ...b, ...p };
    if (b.defaultProps || p.defaultProps) {
      merged.defaultProps = { ...(b.defaultProps || {}), ...(p.defaultProps || {}) };
    }
    if (b.styleOverrides !== undefined || p.styleOverrides !== undefined) {
      merged.styleOverrides = mergeStyleOverrides(b.styleOverrides, p.styleOverrides);
    }
    if (b.variants || p.variants) {
      merged.variants = [...(b.variants || []), ...(p.variants || [])];
    }
    out[name] = merged;
  }
  return out;
}

export default mergeComponents;
